/* 2026-09-18 (owner): полоса «до бесплатной доставки» показывает переход, а не готовый результат.
   Элемент помнит прошлое значение корзины в sessionStorage, при следующей отрисовке начинает с него и доводит
   заполнение и остаток до новых. Разметка уже несёт конечные значения, поэтому без скрипта и при
   prefers-reduced-motion покупатель сразу видит правильные цифры.

   2026-09-21 (owner): два уточнения.
   1) При входе на страницу (прошлого значения нет или оно совпадает) полоса набирается от нуля — видно, на
      какой уровень корзина уже набрана; сумма в корзине набегает, остаток убывает.
   2) Пока полоса едет к взятому порогу, галка не показывается: на элементе держится `is-delivering`
      (грузовик вместо галки, финиш не закрашен). Галка и толчок — ровно в момент, когда заполнение доехало. */
(function (root) {
  'use strict';
  if (!root?.customElements || root.customElements.get('carvion-shipping-progress')) return;
  const KEY = 'carvion-cro-shipping';
  const DURATION_STEP = 620;   // корзина изменилась: едем от прошлого значения
  const DURATION_ENTRY = 900;  // вход на страницу: набираемся от нуля, поэтому дольше

  class ShippingProgress extends root.HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.fill = this.querySelector('.carvion-cro-shipping-fill');
      this.bar = this.querySelector('[role="progressbar"]');
      this.amount = this.querySelector('[data-cro-shipping-amount]');
      this.cartAmount = this.querySelector('[data-cro-shipping-cart]');
      this.finish = this.querySelector('.carvion-cro-shipping-finish');
      this.percent = Math.max(0, Math.min(100, Number(this.dataset.percent) || 0));
      this.remaining = Math.max(0, Number(this.dataset.remaining) || 0);
      this.total = Math.max(0, Number(this.dataset.total) || 0);
      this.threshold = Math.max(0, Number(this.dataset.threshold) || (this.total + this.remaining));
      this.done = this.dataset.done === 'true';

      const still = root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const previous = this.readPrevious();
      this.remember();
      if (still || this.percent <= 0) return;

      // Прошлое значение есть и отличается — едем от него. Иначе это вход на страницу: набираемся от нуля.
      const stepping = previous && previous.percent !== this.percent;
      const from = stepping
        ? {
            percent: previous.percent,
            remaining: typeof previous.remaining === 'number' ? previous.remaining : this.remaining,
            total: typeof previous.total === 'number' ? previous.total : this.total,
          }
        : { percent: 0, remaining: this.threshold, total: 0 };

      if (this.done) this.classList.add('is-delivering');
      // Пока кадры ведёт скрипт, собственный transition полосы только тормозил бы её: заполнение отставало бы
      // от галки, которая теперь ждёт именно приезда.
      this.classList.add('is-scripted');
      this.animate(from, stepping ? DURATION_STEP : DURATION_ENTRY);
    }

    readPrevious() {
      try {
        const raw = sessionStorage.getItem(KEY + ':' + this.dataset.currency);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        return typeof saved?.percent === 'number' ? saved : null;
      } catch { return null; }
    }

    remember() {
      try {
        sessionStorage.setItem(KEY + ':' + this.dataset.currency,
          JSON.stringify({ percent: this.percent, remaining: this.remaining, total: this.total, done: this.done }));
      } catch {}
    }

    // 2026-09-29: деньги пишем тем же форматом, что и сервер. Раньше Intl давал «zł 160.00» и «35,82 €» рядом с серверными
    // «160,00 zł» и «€35,82» — у магазина свои форматы денег. Образец `{{ 123456789 | money }}` лежит в разметке
    // (`data-money-probe`): из него берём символ валюты и его место, разделитель тысяч и дробной части.
    // Нет образца или он не разобрался — Intl, как раньше.
    readFormat() {
      const probe = this.querySelector('[data-money-probe]');
      const text = probe ? probe.textContent.trim() : '';
      const found = text.match(/\d[\d\s.,'’\u00a0\u202f]*\d/);
      if (!found) return null;
      const number = found[0];
      const seps = number.replace(/\d/g, '');
      if (!seps) return null;
      const last = seps[seps.length - 1];
      const decimals = number.length - number.lastIndexOf(last) - 1 === 2;
      const decimal = decimals ? last : '';
      const group = seps.split('').find((c) => c !== decimal) || '';
      return { before: text.slice(0, found.index), after: text.slice(found.index + number.length), decimal, group, decimals };
    }

    money(cents) {
      if (this.format === undefined) this.format = this.readFormat();
      const f = this.format;
      if (f) {
        const value = Math.max(0, cents) / 100;
        const parts = (f.decimals ? value.toFixed(2) : String(Math.round(value))).split('.');
        const whole = f.group ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, f.group) : parts[0];
        return f.before + whole + (parts[1] ? f.decimal + parts[1] : '') + f.after;
      }
      const lang = document.documentElement.lang || 'en';
      const base = { style: 'currency', currency: this.dataset.currency };
      try { return new Intl.NumberFormat(lang, { ...base, currencyDisplay: 'narrowSymbol' }).format(cents / 100); }
      catch { return new Intl.NumberFormat(lang, base).format(cents / 100); }
    }

    // Стартовое состояние ставим синхронно: rAF в скрытой вкладке не срабатывает, и без этого полоса
    // «прыгнула» бы на конечное значение.
    animate(from, duration) {
      this.setFill(from.percent);
      this.setAmounts(from.remaining, from.total);
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        this.setFill(from.percent + (this.percent - from.percent) * eased);
        this.setAmounts(
          Math.round(from.remaining + (this.remaining - from.remaining) * eased),
          Math.round(from.total + (this.total - from.total) * eased)
        );
        if (t < 1) root.requestAnimationFrame(step);
        else this.finishNow();
      };
      root.requestAnimationFrame(step);
      // Запасной срок: вкладка может быть скрытой, тогда кадры не приходят вовсе.
      this.fallback = setTimeout(() => this.finishNow(), duration + 400);
    }

    finishNow() {
      if (this.settled) return;
      this.settled = true;
      clearTimeout(this.fallback);
      this.setFill(this.percent);
      this.setAmounts(this.remaining, this.total);
      // Порог взят — только теперь галка и толчок: сперва полоса доехала, потом награда.
      this.classList.remove('is-delivering');
      if (this.done) this.celebrate();
    }

    setAmounts(remaining, total) {
      if (this.amount) this.amount.textContent = this.money(remaining);
      if (this.cartAmount) this.cartAmount.textContent = this.money(total);
    }

    setFill(percent) {
      if (this.fill) this.fill.style.transform = 'scaleX(' + (percent / 100).toFixed(4) + ')';
      if (this.bar) this.bar.setAttribute('aria-valuenow', String(Math.round(percent)));
    }

    celebrate() {
      if (!this.finish) return;
      this.finish.classList.add('is-reached');
      setTimeout(() => this.finish.classList.remove('is-reached'), 900);
    }
  }
  root.customElements.define('carvion-shipping-progress', ShippingProgress);
})(typeof window === 'undefined' ? null : window);
